import { useEffect, useState, type ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/redux/store';
import { localStorageMethod } from 'src/common/config/FunctionHelper';
import { broadcastService } from 'src/common/services/broadcastService';
import {
  getLocalSeoBusinessId,
  getLocalSeoId,
  setLocalSeoBusinessId,
  setLocalSeoId
} from 'src/common/config/FunctionHelperPartTwo';
import { getLocations as getLocationsAPI } from 'src/common/Scraper';
import {
  setBusinessNamesData,
  setIsConnectionFlow,
  setIsSqSpExtDisconnected,
  setLocationListResponse,
  setLocationsDetailsLoader,
  setReduxFilteredLocationsData,
  setReduxLocationsData,
  setSelectedAccountId,
  setSelectedBusinessAccountId,
  setSelectedBusinessName,
  setSelectedLocationId,
  setTempAccountId,
  setTempLocationId
} from 'src/redux/localSeo';
import { setLocalSeoSection } from 'src/redux/keywordResearch';
import { LocalSeoStepper } from 'src/content/localSeo/LocalSeoStepper';
import { LocalSeoCommonLoader } from 'src/content/localSeo/LocalSeoCommonLoader';

interface GBPConnectionGuardProps {
  children: ReactNode;
}

/**
 * `GBPConnectionGuard` is a wrapper component that gates its children
 * behind Google Business Profile connectivity.
 *
 * **Behaviour:**
 * - On mount it calls `getLocations` to check GBP status and populates
 *   the Redux state that `LocalSeoStepper` and its sub-components rely on.
 * - While the API is in-flight a `LocalSeoCommonLoader` spinner is shown.
 * - If GBP is connected (and a location is selected), `children` are rendered.
 * - Otherwise the `LocalSeoStepper` connection flow is rendered.
 * - Listens on `BroadcastChannel('SEO_Space')` so the Google OAuth popup
 *   can signal a successful auth; then re-fetches locations automatically.
 *
 * Mirrors the essential logic from `LocalSeoDashboard.getLocationsDetails`.
 */
export default function GBPConnectionGuard({
  children
}: GBPConnectionGuardProps) {
  const dispatch = useDispatch();
  const [googleAuthText, setGoogleAuth] = useState<any>(null);

  const locationListResponse: any = useSelector(
    (state: RootState) => state.localSeo.locationListResponse
  );
  const locationsDetailsLoader: any = useSelector(
    (state: RootState) => state.localSeo.locationsDetailsLoader
  );
  const selectedBusinessAccountId: any = useSelector(
    (state: RootState) => state.localSeo.selectedBusinessAccountId
  );
  const isConnectionFlow: any = useSelector(
    (state: RootState) => state.localSeo.isConnectionFlow
  );
  const localSeoSection: any = useSelector(
    (state: RootState) => state.keywordResearch.localSeoSection
  );

  const isGBPConnected = locationListResponse?.isGBPConnected;

  const getLocationsDetails = async (isConnecting = false) => {
    const squareSpaceSiteId = localStorageMethod('get', 'squareSpaceSiteId');
    dispatch(setLocationsDetailsLoader(true));
    const payload = {
      isConnecting,
      selectedAccountId: selectedBusinessAccountId ? selectedBusinessAccountId : null,
      squareSpaceSiteId: squareSpaceSiteId ? squareSpaceSiteId : null
    };
    const getLocationsResponse: any = await getLocationsAPI(payload);

    dispatch(setIsSqSpExtDisconnected(getLocationsResponse?.isSqSpDisconnected));
    dispatch(setLocationListResponse(getLocationsResponse));

    if (
      getLocationsResponse?.status === 'success' &&
      getLocationsResponse?.isGBPConnected
    ) {
      dispatch(setSelectedBusinessAccountId(null));
      const locations = getLocationsResponse?.locations || [];
      const selectedLocations =
        locations?.filter(
          (location: any) =>
            location?.selected === true && location?.temp !== true
        ) || [];

      if (!isConnecting) {
        dispatch(setReduxFilteredLocationsData(selectedLocations));
      }
      dispatch(setReduxLocationsData(locations));

      const locationStorageId = getLocalSeoId();
      const locationStorageBusinessId = getLocalSeoBusinessId();

      const matchedLocation = selectedLocations?.find(
        (location: any) => location?.name === locationStorageId
      );
      dispatch(
        setSelectedBusinessName(
          matchedLocation ? matchedLocation?.title : selectedLocations[0]?.title
        )
      );

      dispatch(
        setTempLocationId(
          locationStorageId ? locationStorageId : selectedLocations[0]?.name
        )
      );
      dispatch(
        setTempAccountId(
          locationStorageBusinessId
            ? locationStorageBusinessId
            : selectedLocations[0]?.accountId
        )
      );

      const businessNames = locations
        ?.map((loc: any) => loc?.title)
        ?.filter(Boolean);
      dispatch(setBusinessNamesData(businessNames));

      if (selectedLocations?.length > 0 && !isConnecting) {
        if (!locationStorageId && !locationStorageBusinessId) {
          setLocalSeoId(selectedLocations[0]?.name);
          setLocalSeoBusinessId(selectedLocations[0]?.accountId);
        }
        dispatch(
          setSelectedLocationId(
            locationStorageId
              ? locationStorageId
              : selectedLocations[0]?.name
          )
        );
        dispatch(
          setSelectedAccountId(
            locationStorageBusinessId
              ? locationStorageBusinessId
              : selectedLocations[0]?.accountId
          )
        );
        dispatch(setLocalSeoSection(2));
      } else if (isConnecting) {
        dispatch(setLocalSeoSection(1));
      }
    } else {
      dispatch(setBusinessNamesData(null));
      dispatch(setSelectedBusinessAccountId(null));
      dispatch(setReduxFilteredLocationsData(null));
      dispatch(setReduxLocationsData(null));
      dispatch(setSelectedBusinessName(null));
      dispatch(setSelectedLocationId(null));
      dispatch(setSelectedAccountId(null));
      dispatch(setTempLocationId(null));
      dispatch(setTempAccountId(null));
      dispatch(
        setLocalSeoSection(getLocationsResponse?.status === 'success' ? 1 : 0)
      );
    }

    dispatch(setIsConnectionFlow(null));
    dispatch(setLocationsDetailsLoader(null));
  };

  // ── On mount: fetch locations ──────────────────────────────────────────
  useEffect(() => {
    getLocationsDetails(!!isConnectionFlow);
  }, []);

  // ── On mount: listen for GBP auth ──────────────────────────────────────
  useEffect(() => {
    broadcastService.subscribe('gbp-connection-guard', {
      'gbpc_auth_connection': (data) => {
        dispatch(setLocalSeoSection(1));
        dispatch(setSelectedBusinessAccountId(data?.accountId));
        setGoogleAuth(data);
      }
    });

    return () => {
      broadcastService.unsubscribe('gbp-connection-guard');
    };
  }, [dispatch]);

  // ── Re-fetch locations after successful auth ──────────────────────────
  useEffect(() => {
    if (
      googleAuthText?.message &&
      googleAuthText?.success &&
      selectedBusinessAccountId
    ) {
      getLocationsDetails(true);
      setTimeout(() => {
        setGoogleAuth(null);
      }, 5000);
    }
  }, [googleAuthText, selectedBusinessAccountId]);

  // ── Render ────────────────────────────────────────────────────────────
  const showChildren =
    isGBPConnected && (localSeoSection === 2 || localSeoSection === undefined);

  if (locationsDetailsLoader) {
    return <LocalSeoCommonLoader />;
  }

  return showChildren ? <>{children}</> : <LocalSeoStepper />;
}
